import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { SlClose } from "react-icons/sl";
import { useFormik } from "formik";
import { Alert, Modal } from "react-bootstrap";
import { Label, Input, Form, Col, Row, FormFeedback } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import PageBanner from "../../layouts/PageBanner.jsx";
import bg from "../../assets/images/banner/bnr4.jpg";
import {
  TempBookingCreate,
  Reset,
} from "../../features/Event/BookingSlice.jsx";
import TicketBookingButton from "../../components/Paypal/TicketBookingButton.jsx.jsx";
import { fetchSelectedVenue } from "../../features/Event/EventTBSlice.jsx";
import moment from "moment";

const FT_VEG = "FT001";
const FT_NONVEG = "FT002";
const FT_BOTH = "FT003";

const FreeEventBooking = () => {
  const dispatch = useDispatch();
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const selectedEvent = JSON.parse(localStorage.getItem("SelectVenue"));

  const { SelectedVenueData, AllTicketperuser } = useSelector(
    (state) => state.EventTBReducer
  );
  const { TempBookindDetails, TempBookingSuccess } = useSelector(
    (state) => state.BookingReducer
  );

  const [PaypalModal, setPaypalModal] = useState(false);
  const [bookingType, setBookingType] = useState(null);
  const [availableSeats, setAvailableSeats] = useState(6);
  const [totalTicketPrice, setTTP] = useState(0);
  const [errormsg, setErrormsg] = useState("");
  const [ticketBookingInfo, setTicketBookingInfo] = useState(null);

  // Adult + Child counts
  const [vegAdult, setVegAdult] = useState(0);
  const [vegChild, setVegChild] = useState(0);
  const [nonVegAdult, setNonVegAdult] = useState(0);
  const [nonVegChild, setNonVegChild] = useState(0);

  // Food only counts
  const [vegOnly, setVegOnly] = useState(0);
  const [nonVegOnly, setNonVegOnly] = useState(0);

  const eventData = SelectedVenueData?.event?.[0];
  const isAdultChildEnabled = eventData?.includeTicketType === "ADULT_CHILD";
  const foodTypeId = eventData?.foodTypeId;
  const isMember = !!userInfo?.member_id;

  const capitalizeFirst = (text = "") =>
    text.trimStart().charAt(0).toUpperCase() + text.trimStart().slice(1);

  /* ================= DATA FETCHING ================= */

  useEffect(() => {
    dispatch(fetchSelectedVenue(selectedEvent));
  }, [dispatch]);

  useEffect(() => {
    if (!SelectedVenueData) return;
    const allocations = SelectedVenueData.ticket_allocations || [];
    const active =
      allocations.find((a) =>
        moment().isBetween(
          moment(a.start_date),
          moment(a.end_date),
          "day",
          "[]"
        )
      ) || allocations[0];
    setBookingType(active);
  }, [SelectedVenueData]);

  useEffect(() => {
    const remaining = AllTicketperuser?.remaining_person_tickets ?? 6;
    setAvailableSeats(Math.min(remaining, 6));
  }, [AllTicketperuser]);

  /* ================= TOTAL CALCULATION ================= */

  useEffect(() => {
    if (!bookingType) return;
    let total = 0;
    if (isAdultChildEnabled) {
      total +=
        vegAdult *
        (isMember
          ? bookingType.f_m_adult_veg_cost || 0
          : bookingType.f_nm_adult_veg_cost || 0);
      total +=
        vegChild *
        (isMember
          ? bookingType.f_m_child_veg_cost || 0
          : bookingType.f_nm_child_veg_cost || 0);
      total +=
        nonVegAdult *
        (isMember
          ? bookingType.f_m_adult_nonveg_cost || 0
          : bookingType.f_nm_adult_nonveg_cost || 0);
      total +=
        nonVegChild *
        (isMember
          ? bookingType.f_m_child_nonveg_cost || 0
          : bookingType.f_nm_child_nonveg_cost || 0);
    } else {
      total +=
        vegOnly *
        (isMember
          ? bookingType.f_m_vegcost || 0
          : bookingType.f_nm_vegcost || 0);
      total +=
        nonVegOnly *
        (isMember
          ? bookingType.f_m_nonvegcost || 0
          : bookingType.f_nm_nonvegcost || 0);
    }
    setTTP(total);
  }, [
    vegAdult,
    vegChild,
    nonVegAdult,
    nonVegChild,
    vegOnly,
    nonVegOnly,
    bookingType,
    isMember,
    isAdultChildEnabled,
  ]);

  /* ================= VALIDATION HANDLER ================= */
  const handleCountChange = (setter, currentVal, newVal) => {
    const value = Math.max(0, parseInt(newVal) || 0);
    setErrormsg("");

    if (value > 6) {
      setErrormsg("Maximum 6 allowed per category");
      return;
    }

    setter(value);
  };

  /* ================= FORMIK SETUP ================= */

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstname: userInfo?.firstname || "",
      lastname: userInfo?.lastname || "",
      email: userInfo?.email || "",
      mobile: userInfo?.mobile || "",
    },
    validationSchema: Yup.object({
      firstname: Yup.string().required("First name is required"),
      lastname: Yup.string().required("Last name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      mobile: Yup.string().required("Mobile is required"),
    }),
    onSubmit: (values) => {
      if (totalTicketPrice === 0) {
        setErrormsg("Please select at least one ticket");
        return;
      }

      // ✅ DETECT FREE EVENT + PAID FOOD
      const isFreePaidFood =
        SelectedVenueData?.event?.[0]?.type === "FREE" &&
        SelectedVenueData?.event?.[0]?.freeFoodType === "PAID_FOOD";

      // ----------------------------
      // FOOD COUNTS RESOLUTION
      // ----------------------------
      const resolvedVegAdult = isAdultChildEnabled ? vegAdult : 0;
      const resolvedVegChild = isAdultChildEnabled ? vegChild : 0;
      const resolvedNonVegAdult = isAdultChildEnabled ? nonVegAdult : 0;
      const resolvedNonVegChild = isAdultChildEnabled ? nonVegChild : 0;

      const resolvedVegCount = isAdultChildEnabled
        ? vegAdult + vegChild
        : isFreePaidFood
        ? vegOnly
        : 0;

      const resolvedNonVegCount = isAdultChildEnabled
        ? nonVegAdult + nonVegChild
        : isFreePaidFood
        ? nonVegOnly
        : 0;

      dispatch(
        TempBookingCreate({
          ...values,
          memberID: userInfo?._id,
          master_event_id: SelectedVenueData.master_event_id,
          master_venue_id: SelectedVenueData._id,

          event_type: eventData?.type, // FREE / PAID
          freeFoodOption: eventData?.freeFoodOption, // WITH_FOOD / WITHOUT_FOOD
          freeFoodType: eventData?.freeFoodType, // PAID_FOOD
          includeTicketType: eventData?.includeTicketType,

          event_name: eventData?.name || SelectedVenueData?.event?.[0]?.name,
          venue_name: SelectedVenueData?.venue,
          event_date: SelectedVenueData?.start_datetime,

          // ---- ADULT / CHILD (ONLY WHEN ENABLED) ----
          veg_adult_count: resolvedVegAdult,
          veg_child_count: resolvedVegChild,
          nonveg_adult_count: resolvedNonVegAdult,
          nonveg_child_count: resolvedNonVegChild,

          // ---- FOOD TOTALS (ALWAYS CORRECT) ----
          veg_count: resolvedVegCount,
          nonveg_count: resolvedNonVegCount,

          net_amount: totalTicketPrice,
        })
      );
    },
  });

  useEffect(() => {
    if (TempBookingSuccess) {
      setTicketBookingInfo(TempBookindDetails);
      setPaypalModal(true);
      dispatch(Reset());
    }
  }, [TempBookingSuccess, TempBookindDetails, dispatch]);

  if (!SelectedVenueData || !bookingType) return null;

  return (
    <div className="page-content bg-white">
      <PageBanner maintitle="Home" pagetitle="Event Booking" background={bg} />

      <div className="container mt-4 mb-5">
        <Row>
          {/* Left Panel: Event Summary */}
          <Col lg={4} className="mb-4">
            <div
              className="p-4 border rounded bg-light shadow-sm sticky-top"
              style={{ top: "100px", zIndex: 1 }}
            >
              <h4 className="text-primary">{eventData?.name}</h4>
              <p className="text-muted mb-1">
                <i className="fa fa-map-marker me-2"></i>
                {SelectedVenueData.venue}
              </p>
              <hr />

              <div className="pricing-info small">
                <h6 className="mb-2">
                  Pricing for {isMember ? "Members" : "Non-Members"}:
                </h6>

                {isAdultChildEnabled ? (
                  <ul className="list-unstyled mb-0">
                    {/* Veg Adult */}
                    {(isMember
                      ? bookingType.f_m_adult_veg_cost
                      : bookingType.f_nm_adult_veg_cost) != null && (
                      <li>
                        {eventData?.short_veg_dec && (
                          <p
                            className="mb-1 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {capitalizeFirst(eventData.short_veg_dec)}
                          </p>
                        )}
                        Veg Adult:{" "}
                        <strong>
                          $
                          {isMember
                            ? bookingType.f_m_adult_veg_cost
                            : bookingType.f_nm_adult_veg_cost}
                        </strong>
                      </li>
                    )}

                    {/* Veg Child */}
                    {(isMember
                      ? bookingType.f_m_child_veg_cost
                      : bookingType.f_nm_child_veg_cost) != null && (
                      <li>
                        Veg Child:{" "}
                        <strong>
                          $
                          {isMember
                            ? bookingType.f_m_child_veg_cost
                            : bookingType.f_nm_child_veg_cost}
                        </strong>
                      </li>
                    )}

                    {/* Non-Veg Adult */}
                    {(isMember
                      ? bookingType.f_m_adult_nonveg_cost
                      : bookingType.f_nm_adult_nonveg_cost) != null && (
                      <li>
                        {eventData?.short_nonveg_dec && (
                          <p
                            className="mb-1 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {capitalizeFirst(eventData.short_nonveg_dec)}
                          </p>
                        )}
                        Non-Veg Adult:{" "}
                        <strong>
                          $
                          {isMember
                            ? bookingType.f_m_adult_nonveg_cost
                            : bookingType.f_nm_adult_nonveg_cost}
                        </strong>
                      </li>
                    )}

                    {/* Non-Veg Child */}
                    {(isMember
                      ? bookingType.f_m_child_nonveg_cost
                      : bookingType.f_nm_child_nonveg_cost) != null && (
                      <li>
                        Non-Veg Child:{" "}
                        <strong>
                          $
                          {isMember
                            ? bookingType.f_m_child_nonveg_cost
                            : bookingType.f_nm_child_nonveg_cost}
                        </strong>
                      </li>
                    )}
                  </ul>
                ) : (
                  <div  >
                    {(isMember
                      ? bookingType.f_m_vegcost
                      : bookingType.f_nm_vegcost) != null && (
                    //   <span>
                     <div className="mb-2">
                        {eventData?.short_veg_dec && (
                          <p
                            className="mb-1 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {capitalizeFirst(eventData.short_veg_dec)}
                          </p>
                        )}
                        Veg:{" "}
                        <strong>
                          $
                          {isMember
                            ? bookingType.f_m_vegcost
                            : bookingType.f_nm_vegcost}
                        </strong>
                      {/* </span> */}
                      </div>
                    )}

                    {(isMember
                      ? bookingType.f_m_nonvegcost
                      : bookingType.f_nm_nonvegcost) != null && (
                    //   <span>
                    <div className="mb-2">
                        {eventData?.short_nonveg_dec && (
                          <p
                            className="mb-1 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {capitalizeFirst(eventData.short_nonveg_dec)}
                          </p>
                        )}
                        Non-Veg:{" "}
                        <strong>
                          $
                          {isMember
                            ? bookingType.f_m_nonvegcost
                            : bookingType.f_nm_nonvegcost}
                        </strong>
                      {/* </span> */}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Col>

          {/* Right Panel: Form */}
          <Col lg={8}>
            <Form
              onSubmit={validation.handleSubmit}
              className="p-4 border rounded shadow-sm bg-white"
            >
              <h4 className="mb-4 border-bottom pb-2">Information</h4>
              <Row>
                <Col md={6} className="mb-3">
                  <Label>First Name</Label>
                  <Input
                    {...validation.getFieldProps("firstname")}
                    invalid={
                      validation.touched.firstname &&
                      !!validation.errors.firstname
                    }
                    placeholder="Enter first name"
                  />
                  <FormFeedback>{validation.errors.firstname}</FormFeedback>
                </Col>
                <Col md={6} className="mb-3">
                  <Label>Last Name</Label>
                  <Input
                    {...validation.getFieldProps("lastname")}
                    invalid={
                      validation.touched.lastname &&
                      !!validation.errors.lastname
                    }
                    placeholder="Enter last name"
                  />
                  <FormFeedback>{validation.errors.lastname}</FormFeedback>
                </Col>
              </Row>
              <Row>
                <Col md={6} className="mb-3">
                  <Label>Email Address</Label>
                  <Input
                    {...validation.getFieldProps("email")}
                    invalid={
                      validation.touched.email && !!validation.errors.email
                    }
                    placeholder="email@example.com"
                  />
                  <FormFeedback>{validation.errors.email}</FormFeedback>
                </Col>
                <Col md={6} className="mb-3">
                  <Label>Mobile Number</Label>
                  <Input
                    {...validation.getFieldProps("mobile")}
                    invalid={
                      validation.touched.mobile && !!validation.errors.mobile
                    }
                    placeholder="Enter mobile number"
                  />
                  <FormFeedback>{validation.errors.mobile}</FormFeedback>
                </Col>
              </Row>

              <h5 className="mt-4 mb-3 border-bottom pb-2">Select Food</h5>

              {isAdultChildEnabled ? (
                <div className="food-selection-grid">
                  <Row>
                    {(foodTypeId === FT_VEG || foodTypeId === FT_BOTH) && (
                      <Col md={6} className="mb-3">
                        <div className="p-3 border-start border-4 border-success rounded bg-light">
                          <h6 className="text-success fw-bold mb-3">
                            Vegetarian Option
                          </h6>
                          <Row>
                            <Col>
                              <Label size="sm">Adults</Label>
                              <Input
                                type="number"
                                min="0"
                                value={vegAdult}
                                onChange={(e) =>
                                  handleCountChange(
                                    setVegAdult,
                                    vegAdult,
                                    e.target.value
                                  )
                                }
                              />
                            </Col>
                            <Col>
                              <Label size="sm">Children</Label>
                              <Input
                                type="number"
                                min="0"
                                value={vegChild}
                                onChange={(e) =>
                                  handleCountChange(
                                    setVegChild,
                                    vegChild,
                                    e.target.value
                                  )
                                }
                              />
                            </Col>
                          </Row>
                        </div>
                      </Col>
                    )}

                    {(foodTypeId === FT_NONVEG || foodTypeId === FT_BOTH) && (
                      <Col md={6} className="mb-3">
                        <div className="p-3 border-start border-4 border-danger rounded bg-light">
                          <h6 className="text-danger fw-bold mb-3">
                            Non-Vegetarian Option
                          </h6>
                          <Row>
                            <Col>
                              <Label size="sm">Adults</Label>
                              <Input
                                type="number"
                                min="0"
                                value={nonVegAdult}
                                onChange={(e) =>
                                  handleCountChange(
                                    setNonVegAdult,
                                    nonVegAdult,
                                    e.target.value
                                  )
                                }
                              />
                            </Col>
                            <Col>
                              <Label size="sm">Children</Label>
                              <Input
                                type="number"
                                min="0"
                                value={nonVegChild}
                                onChange={(e) =>
                                  handleCountChange(
                                    setNonVegChild,
                                    nonVegChild,
                                    e.target.value
                                  )
                                }
                              />
                            </Col>
                          </Row>
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              ) : (
                <div className="p-3 border rounded bg-light">
                  <Row>
                    {(foodTypeId === FT_VEG || foodTypeId === FT_BOTH) && (
                      <Col md={6}>
                        <Label className="fw-bold">Veg Count</Label>
                        <Input
                          type="number"
                          min="0"
                          value={vegOnly}
                          onChange={(e) =>
                            handleCountChange(
                              setVegOnly,
                              vegOnly,
                              e.target.value
                            )
                          }
                        />
                      </Col>
                    )}
                    {(foodTypeId === FT_NONVEG || foodTypeId === FT_BOTH) && (
                      <Col md={6}>
                        <Label className="fw-bold">Non-Veg Count</Label>
                        <Input
                          type="number"
                          min="0"
                          value={nonVegOnly}
                          onChange={(e) =>
                            handleCountChange(
                              setNonVegOnly,
                              nonVegOnly,
                              e.target.value
                            )
                          }
                        />
                      </Col>
                    )}
                  </Row>
                </div>
              )}

              {errormsg && (
                <Alert color="danger" className="mt-3 py-2">
                  {errormsg}
                </Alert>
              )}

              <div className="mt-4 d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted fw-bold">Total Amount:</small>
                  <h4 className="mb-0">${totalTicketPrice}</h4>
                </div>

                <button type="submit" className="btn btn-primary px-4 fw-bold">
                  Confirm Booking
                </button>
              </div>
            </Form>
          </Col>
        </Row>
      </div>

      {/* Payment Modal */}
      <Modal
        className="modal fade modal-wrapper auth-modal"
        show={PaypalModal}
        onHide={() => setPaypalModal(false)}
        centered
        backdrop="static"
      >
        <h2 className="title">
          Payment Process{" "}
          <span
            className="position-absolute top-0 start-100 translate-middle text-light  rounded-circle"
            style={{
              fontSize: "2rem",
              cursor: "pointer",
            }}
            onClick={() => {
              setPaypalModal(false);
            }}
          >
            <SlClose />
          </span>
        </h2>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            //ValidationRegister.handleSubmit()
          }}
        >
          <TicketBookingButton
            data2={ticketBookingInfo}
            setPaypalModal={setPaypalModal}
          />
          {/* <PaymentPaypal data2={validation.values} /> */}
        </Form>
      </Modal>
    </div>
  );
};

export default FreeEventBooking;
